import { isDefined } from "@clipboard-health/util-ts";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
} from "@mui/material";
import { Text, useModalState } from "@src/lib/ui-react";

import { useGetTen99PolicyConfiguration } from "./api/useGetTen99PolicyConfiguration";
import { CATEGORY_CODE_DISPLAY_NAMES } from "./constants";
import { EnableTen99PolicyDialog } from "./EnableTen99PolicyDialog";

interface Ten99PolicyCoverageCardProps {
  workplaceId: string;
}

export function Ten99PolicyCoverageCard(props: Ten99PolicyCoverageCardProps) {
  const { workplaceId } = props;
  const enableDialogModalState = useModalState();

  const {
    data: configData,
    isLoading,
    isError,
    refetch,
  } = useGetTen99PolicyConfiguration(workplaceId);

  if (isLoading) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={20} />
            <Text variant="body2">Loading 1099 Policy configuration...</Text>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Alert severity="error">Failed to load 1099 Policy configuration.</Alert>
        </CardContent>
      </Card>
    );
  }

  if (!isDefined(configData)) {
    return null;
  }

  const config = configData.data.attributes;
  const canOpenDialog =
    isDefined(config.suggestedCategoryCode) && isDefined(config.documentRequirementId);

  if (config.entityExists) {
    const categoryCodeDisplay = isDefined(config.ten99PolicyJobCategoryCode)
      ? (CATEGORY_CODE_DISPLAY_NAMES[config.ten99PolicyJobCategoryCode] ??
        config.ten99PolicyJobCategoryCode)
      : "Unknown";

    return (
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Text variant="subtitle1" sx={{ fontWeight: "bold" }}>
                Workers&apos; Compensation Coverage (1099Policy)
              </Text>
              <Chip label="Enabled" color="success" size="small" />
            </Stack>
            <Text variant="body2" color="text.secondary">
              Entity ID: {config.ten99PolicyEntityId}
            </Text>
            <Text variant="body2" color="text.secondary">
              Category: {categoryCodeDisplay}
            </Text>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (!config.stateOnboarded) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Text variant="subtitle1" sx={{ fontWeight: "bold" }}>
              Workers&apos; Compensation Coverage (1099Policy)
            </Text>
            <Alert severity="warning">
              This facility&apos;s state is not yet configured for 1099Policy coverage. The document
              requirement and feature flag must be set up for this state before enabling coverage.
            </Alert>
            <Box>
              <Button disabled variant="contained">
                Enable Coverage
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Text variant="subtitle1" sx={{ fontWeight: "bold" }}>
              Workers&apos; Compensation Coverage (1099Policy)
            </Text>
            <Text variant="body2" color="text.secondary">
              This facility&apos;s state is onboarded for 1099Policy coverage. Click the button
              below to create the entity and requirement mapping.
            </Text>
            <Box>
              <Button
                disabled={!canOpenDialog}
                variant="contained"
                color="primary"
                onClick={enableDialogModalState.openModal}
              >
                Enable Coverage
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
      {isDefined(config.suggestedCategoryCode) && isDefined(config.documentRequirementId) && (
        <EnableTen99PolicyDialog
          modalState={enableDialogModalState}
          documentRequirementId={config.documentRequirementId}
          suggestedCategoryCode={config.suggestedCategoryCode}
          workplaceId={workplaceId}
          onSuccess={() => {
            void refetch();
          }}
        />
      )}
    </>
  );
}
